import { AppShell, Box } from '@mantine/core';
import { ErrorBoundary, useMedplum } from '@medplum/react';
import { Suspense, useEffect, useState, type JSX } from 'react';
import { Navbar } from './components/Navbar';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Router } from './Router';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import Header2 from './components/Header.feelheard';
import Footer2 from './components/Footer.feelheard';
import { Loading } from './components/Loading';
import { RegisterPage } from './pages/RegisterPage';
import { SignInPage } from './pages/SignInPage';
import { LandingPage } from './pages/landing';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DocumentsProvider } from './contexts/DocumentsContext';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import Cookies from './components/Cookies';
import DataProtection from './components/DataProtection';
import AIEthics from './components/AIEthics';
import AccountDeletion from './components/AccountDeletion';
import HowItWorksPage from './pages/HowItWorksPage';
import BenefitsPage from './pages/BenefitsPage';
import FAQPage from './pages/FAQPage';
import { JoinRedirect } from './pages/JoinRedirect';
import { GoogleTagManager } from './components/GoogleTagManager';
import FacebookPixel from './components/FacebookPixel';

// Set up environment variable for Stripe if not already defined
// This is for development purposes only
if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
  (window as any).VITE_STRIPE_PUBLISHABLE_KEY = 'pk_test_placeholder';
}

// TailwindStyles component removed to prevent global style overrides

// Import the function from main.tsx to keep DRY
// This is the same function defined in main.tsx but enhanced to handle URL parameters
const isAuthExemptPath = (path: string): boolean => {
  // Extract the base path without query parameters
  const basePath = path.split('?')[0];
  
  return basePath === '/' ||
         basePath === '/signin' ||
         basePath === '/register' ||
         basePath === '/resetpassword' ||
         basePath.startsWith('/setpassword/') ||
         basePath === '/terms' ||
         basePath === '/privacy' ||
         basePath === '/cookies' ||
         basePath === '/data-protection' ||
         basePath === '/ai-ethics' ||
         basePath === '/how-it-works' ||
         basePath === '/benefits' ||
         basePath === '/faq' ||
         basePath === '/account-deletion';
};

export function App(): JSX.Element {
  const medplum = useMedplum();
  const currentPath = window.location.pathname;
  
  // Move useState hooks here - they must be called on every render
  const [navbarOpened, setNavbarOpened] = useState(false);
  const [isFullscreenMobile, setIsFullscreenMobile] = useState(false);
  
  // Add Smart App Banner for iOS Safari
  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = 'apple-itunes-app';
    metaTag.content = 'app-id=6746776325';
    document.head.appendChild(metaTag);
    
    return () => {
      // Clean up on unmount
      const existingTag = document.querySelector('meta[name="apple-itunes-app"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);
  
  // Monitor the data-mobile-fullscreen attribute to detect fullscreen mode
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-mobile-fullscreen') {
          setIsFullscreenMobile(document.body.hasAttribute('data-mobile-fullscreen'));
        }
      });
    });
    
    observer.observe(document.body, { attributes: true });
    
    // Initial check
    setIsFullscreenMobile(document.body.hasAttribute('data-mobile-fullscreen'));
    
    return () => observer.disconnect();
  }, []);
  
  // Add tracking components (only activate on safe routes)
  // These run on all routes, but only inject tracking code on non-PHI pages
  
  // If Medplum is still loading, but the page is a public page,
  // we should render the public route instead of showing nothing
  if (medplum.isLoading()) {
    if (isAuthExemptPath(currentPath)) {
      // Render public routes even during loading
      return (
        <>
          <GoogleTagManager />
          <FacebookPixel />
          {renderPublicRoutes()}
        </>
      );
    }
    // For authenticated routes, show loading state or fallback
    return (
      <Loading />
    );
  }
  
  return (
    <>
      <GoogleTagManager />
      <FacebookPixel />
      {renderApp()}
    </>
  );
  
  function renderPublicRoutes() {
    return (
      <Routes>
        <Route path="/" element={
          <>
            <Header2 />
            <main className="pt-16">
              <LandingPage />
            </main>
            <Footer2 />
          </>
        } />
        <Route path="signin" element={<SignInPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="setpassword/:id/:secret" element={<SetPasswordPage />} />
        <Route path="resetpassword" element={<ResetPasswordPage />} />
        <Route path="terms" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><Terms /></main>
            <Footer2 />
          </>
        } />
        <Route path="privacy" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><Privacy /></main>
            <Footer2 />
          </>
        } />
        <Route path="cookies" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><Cookies /></main>
            <Footer2 />
          </>
        } />
        <Route path="data-protection" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><DataProtection /></main>
            <Footer2 />
          </>
        } />
        <Route path="ai-ethics" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><AIEthics /></main>
            <Footer2 />
          </>
        } />
        <Route path="how-it-works" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><HowItWorksPage /></main>
            <Footer2 />
          </>
        } />
        <Route path="benefits" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><BenefitsPage /></main>
            <Footer2 />
          </>
        } />
        <Route path="faq" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><FAQPage /></main>
            <Footer2 />
          </>
        } />
        <Route path="account-deletion" element={
          <>
            <Header2 />
            <main className="pt-20 pb-10"><AccountDeletion /></main>
            <Footer2 />
          </>
        } />
        <Route path="join/:threadId" element={<JoinRedirect />} />
        <Route path="join" element={<JoinRedirect />} />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    );
  }
  
  function renderApp() {
    const isAuthenticated = !!medplum.getProfile();
  
  // For unauthenticated users
  if (!isAuthenticated) {
    return (
      <>
        <Routes>
         <Route path="/" element={
            <>
              <Header2 />
              <main className="pt-16">
                <LandingPage />
              </main>
              <Footer2 />
            </>
          } />
          <Route path="signin" element={<SignInPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="setpassword/:id/:secret" element={<SetPasswordPage />} />
          <Route path="resetpassword" element={<ResetPasswordPage />} />
          <Route path="terms" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><Terms /></main>
              <Footer2 />
            </>
          } />
          <Route path="privacy" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><Privacy /></main>
              <Footer2 />
            </>
          } />
          <Route path="cookies" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><Cookies /></main>
              <Footer2 />
            </>
          } />
          <Route path="data-protection" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><DataProtection /></main>
              <Footer2 />
            </>
          } />
          <Route path="ai-ethics" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><AIEthics /></main>
              <Footer2 />
            </>
          } />
          <Route path="how-it-works" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><HowItWorksPage /></main>
              <Footer2 />
            </>
          } />
          <Route path="benefits" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><BenefitsPage /></main>
              <Footer2 />
            </>
          } />
          <Route path="faq" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><FAQPage /></main>
              <Footer2 />
            </>
          } />
          <Route path="account-deletion" element={
            <>
              <Header2 />
              <main className="pt-20 pb-10"><AccountDeletion /></main>
              <Footer2 />
            </>
          } />
          <Route path="join/:threadId" element={<JoinRedirect />} />
          <Route path="join" element={<JoinRedirect />} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </>
    );
  }

  // Special case handling for authenticated users accessing auth-related pages
  if (currentPath.startsWith('/setpassword/') || 
      currentPath === '/resetpassword' || 
      currentPath === '/signin' ||
      currentPath.startsWith('/oauth/callback')) {
    
    return (
      <>
        <Routes>
          <Route path="setpassword/:id/:secret" element={<SetPasswordPage />} />
          <Route path="resetpassword" element={<ResetPasswordPage />} />
          <Route path="signin" element={<SignInPage />} />
          <Route path="oauth/callback" element={
            <Navigate replace to="/" />
          } />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </>
    );
  }
  
  // Normal authenticated app
  
  return (
    <>
      <DocumentsProvider>
        <AppShell
          header={{ height: 80 }}
          navbar={{ 
            width: 300, 
            breakpoint: 'sm', 
            collapsed: { mobile: !navbarOpened, desktop: true }
          }}
          padding={isFullscreenMobile ? 0 : "md"} // Remove padding in fullscreen mode
          footer={{ height: isFullscreenMobile ? 0 : 60 }}
        >
          <Header onBurgerClick={() => setNavbarOpened((o) => !o)} />
          <Navbar opened={navbarOpened} onClose={() => setNavbarOpened(false)} />
          <AppShell.Main>
            <ErrorBoundary>
              <Suspense fallback={<Loading />}>
                <Router navbarState={{ opened: navbarOpened, setOpened: setNavbarOpened }} />
              </Suspense>
            </ErrorBoundary>
          </AppShell.Main>
          {!isFullscreenMobile && <Footer />}
        </AppShell>
      </DocumentsProvider>
    </>
  );
  }
}